import { useContext, useState } from 'react';
import { Popover } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { ActionContext } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/ActionContext';

interface Props {
	renderContent: (close: () => void) => React.ReactNode | null;
	isVisible: boolean;
}

export const BreakdownAction = ({ renderContent, isVisible }: Props) => {
	const { onOpenChange } = useContext(ActionContext);
	const [opened, setOpened] = useState(false);
	const [isIconHovered, setIsIconHovered] = useState(false);
	let iconColor: string;
	if (opened) {
		iconColor = defaultTheme.v3.colors.dark[8];
	} else if (isIconHovered) {
		iconColor = defaultTheme.v3.colors.blue[6];
	} else {
		iconColor = defaultTheme.v3.colors.dark[0];
	}

	if (!opened && !isVisible) {
		return null;
	}

	let iconBackground: string;
	if (opened) {
		iconBackground = defaultTheme.v3.colors.gray[4];
	} else if (isIconHovered) {
		iconBackground = defaultTheme.v3.colors.gray[2];
	} else {
		iconBackground = 'none';
	}

	const content = opened
		? renderContent(() => {
				setOpened(false);
				onOpenChange?.(false);
			})
		: null;

	return (
		<Popover
			opened={opened && content !== null}
			closeOnClickOutside={false}
			position="bottom-start"
			shadow="md"
			withinPortal
		>
			<Popover.Target>
				<div
					role="button"
					tabIndex={0}
					onClick={(e) => {
						e.stopPropagation();
						setOpened((prev) => {
							const next = !prev;
							onOpenChange?.(next);
							return next;
						});
					}}
					onMouseEnter={() => setIsIconHovered(true)}
					onMouseLeave={() => setIsIconHovered(false)}
					style={{
						background: iconBackground,
						border: 'none',
						borderRadius: '0.25rem',
						padding: '0.125rem',
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
					}}
				>
					<Icon variant="calculator" size="lg" color={iconColor} fillStroke />
				</div>
			</Popover.Target>
			{content && (
				<Popover.Dropdown
					style={{ padding: 0, border: 'none', background: 'transparent' }}
					onClick={(e: React.MouseEvent) => e.stopPropagation()}
					onMouseMove={(e: React.MouseEvent) => e.stopPropagation()}
				>
					{content}
				</Popover.Dropdown>
			)}
		</Popover>
	);
};
